// ─── useDataGrid — the "one wiring hook" for a DataGrid table ─────────────────
//
// Collapses the mechanical, near-identical boilerplate every DataGrid
// consumer (PropertiesTable, TransactionsTableV3, …) otherwise re-implements
// by hand: preset persistence (`useGridPresets`) + the standard two-stage
// filter/sort pipeline (group-quick-filters → per-column filters → free-text
// search → sort) + footer aggregates + the sort/filter/group-toggle preset
// handlers + the "which columns are actually visible" derivation.
//
// The consumer keeps ownership of everything that is genuinely table-specific
// — its `ColumnMeta` map, its cell/header/footer renderers, its data source,
// its chrome (title bar, map integration, etc.). This hook only owns the
// wiring in between, so a new table is "define columns + renderers, call
// useDataGrid, render <DataGridPresetBar>/<DataGridTable> with what it
// returns" rather than ~60 lines of copy-pasted `useMemo`s that silently
// drift apart (e.g. one table forgetting `aggregates`, or the two-stage
// filter split, or a group-toggle handler).

import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import {
  computeColumnAggregates,
  filterGridItems,
  filterGridItemsByGroups,
  getEnumFilterOptions,
  sortGridItems
} from './gridEngine';
import type { ColumnConfig, ColumnMetaMap, FilterValue, GroupConfig } from './types';
import { useGridPresets, type UseGridPresetsResult } from './useGridPresets';

/**
 * Options for the `useDataGrid` hook, specifying row data, column metadata, preset keys, grouping configuration, and search behavior.
 */
export interface UseDataGridOptions<TRow, K extends string> {
  /** The full row set (already fetched by the consumer). */
  rows: TRow[];
  /** This table's column metadata map. */
  columnMeta: ColumnMetaMap<TRow, K>;
  /** Default (fresh) column configuration — seeds the Default/Last presets. */
  defaultColumns: ColumnConfig<K>[];
  /** Preference key for the saved-presets list (must be unique per table). */
  presetsKey: string;
  /** Preference key for the active-preset id (must be unique per table). */
  activeIdKey: string;
  /**
   * Quick-filter "stat card" group axes (e.g. Division / Management Type /
   * Phase). The `groupFieldByGroupId` map that `filterGridItemsByGroups`
   * needs is derived from these automatically (`group.id -> group.field`) —
   * no separate hand-maintained constant to keep in sync. Defaults to none.
   */
  groupConfigs?: GroupConfig<K>[];
  /**
   * Free-text search predicate — returns true if `row` matches the
   * already-lowercased query `q`. Omit to disable free-text search entirely.
   * (Kept as a consumer callback rather than derived from `columnMeta`
   * because which fields are searchable, and how, is table-specific — e.g.
   * TransactionsTableV3 searches a computed status label, not a raw field.)
   */
  searchPredicate?: (row: TRow, lowerCaseQuery: string) => boolean;
  /**
   * Extra filter/sort keys that must survive preset reconciliation despite
   * not being in `defaultColumns` — for a combined column whose underlying
   * fields are independently filterable/sortable. Forwarded verbatim to
   * `useGridPresets`. See its `additionalValidFilterSortKeys` doc. Pass a
   * stable (module-level or memoized) array reference.
   */
  additionalValidFilterSortKeys?: string[];
  /** App preference scope. Defaults to the global 'Prism' scope. */
  appValue?: string;
}

/**
 * Return type of `useDataGrid`, providing the complete data-grid pipeline: sorted rows, groups, aggregates, filters, and handlers.
 */
export interface UseDataGridResult<TRow, K extends string> extends UseGridPresetsResult<K> {
  /** Alias of `activePreset` — the working preset all derivations read from. */
  preset: UseGridPresetsResult<K>['activePreset'];

  /** Free-text search box state. */
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  /**
   * Group axes with their option lists filled in live from `rows` when a
   * given `GroupConfig` supplied no explicit `options` — so quick-filter
   * cards reflect the values that actually exist in the data rather than a
   * hardcoded list. A config that DID supply `options` is passed through
   * untouched.
   */
  groupConfigs: GroupConfig<K>[];

  /** Columns from the active preset that still exist in `columnMeta` and aren't `additionalValidFilterSortKeys` (filter/sort-only) keys. */
  visibleColumns: ColumnConfig<K>[];

  /**
   * Rows after the group quick-filters ONLY (not per-column filters / search)
   * — the correct source for column filter dropdown options/counts, so
   * selecting one option in a column's own filter doesn't make that column's
   * OTHER options vanish. This is what a header/cell context's `rows` should
   * be.
   */
  groupFilteredRows: TRow[];
  /** Fully filtered + free-text-searched + sorted rows — what the table body renders. */
  sortedRows: TRow[];
  /** Footer aggregates over `sortedRows` (formatted), keyed by column. */
  aggregates: Partial<Record<K, string>>;
  /**
   * True when any column filter, range filter, group quick-filter, or the
   * free-text search is currently narrowing the row set — the same
   * predicate `DataGridPresetBar` uses internally for its "Clear"/"Filtered
   * by:" affordances, lifted here as a single source of truth so consumers
   * (e.g. a map integration that should only show a filtered subset of pins
   * when the table is actually filtered) don't have to re-derive it.
   */
  hasActiveFilters: boolean;

  /** Applies a single-column sort to the preset (`'none'` clears sorting). */
  handleSort: (columnKey: string, order: 'asc' | 'desc' | 'none') => void;
  /** Sets/replaces one column's filter value in the preset. */
  handleFilterChange: (columnKey: string, value: FilterValue) => void;
  /**
   * Toggles a column as the row-grouping column: sets it as the group-by
   * column, or clears grouping if it's already the group-by column.
   */
  handleToggleGroup: (columnKey: K) => void;
}

/**
 * The single wiring hook backing a DataGrid table — see the file header for
 * the full rationale. Owns preset persistence, the two-stage
 * filter/sort/search pipeline, footer aggregates, live quick-filter option
 * derivation, and the standard preset handlers, so a consuming table only has
 * to supply its columns/renderers/data and render `DataGridPresetBar` /
 * `DataGridTable` with the returned values.
 */
export function useDataGrid<TRow, K extends string>({
  rows,
  columnMeta,
  defaultColumns,
  presetsKey,
  activeIdKey,
  groupConfigs: groupConfigsInput,
  searchPredicate,
  additionalValidFilterSortKeys,
  appValue,
}: UseDataGridOptions<TRow, K>): UseDataGridResult<TRow, K> {
  const presetApi = useGridPresets<K>({
    presetsKey,
    activeIdKey,
    defaultColumns,
    additionalValidFilterSortKeys,
    appValue,
  });
  const preset = presetApi.activePreset;
  const { patchActivePreset } = presetApi;

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  // Filter/sort-only keys are, by definition, never their own visible column
  // (they back a combined column instead), so they must be excluded from the
  // visible-column list even though they ARE legitimate `columnMeta` entries
  // — `key in columnMeta` alone wouldn't drop them.
  const underlyingOnlyKeys = useMemo(
    () => new Set(additionalValidFilterSortKeys ?? []),
    [additionalValidFilterSortKeys],
  );

  // group.id -> group.field, derived from the supplied group configs — the
  // map `filterGridItemsByGroups` needs, no longer a separate constant the
  // consumer maintains (and can forget to keep aligned with its cards).
  const groupFieldByGroupId = useMemo(() => {
    const map: Record<string, K> = {};
    for (const g of groupConfigsInput ?? []) map[g.id] = g.field;
    return map;
  }, [groupConfigsInput]);

  // Fill in each group axis's option list live from the data when the config
  // didn't hardcode one — see `groupConfigs` in the result type.
  const groupConfigs = useMemo<GroupConfig<K>[]>(() => {
    if (!groupConfigsInput) return [];
    return groupConfigsInput.map((group) =>
      group.options && group.options.length > 0
        ? group
        : { ...group, options: getEnumFilterOptions(rows, group.field, columnMeta).map((value) => ({ value, label: value })) },
    );
  }, [groupConfigsInput, rows, columnMeta]);

  const visibleColumns = useMemo(
    () => preset.columns.filter((c) => c.key in columnMeta && !underlyingOnlyKeys.has(c.key)),
    [preset.columns, columnMeta, underlyingOnlyKeys],
  );

  const groupFilteredRows = useMemo(
    () => filterGridItemsByGroups(rows, preset.groupFilters, groupFieldByGroupId, columnMeta),
    [rows, preset.groupFilters, groupFieldByGroupId, columnMeta],
  );

  const filteredRows = useMemo(() => {
    const columnFiltered = filterGridItems(groupFilteredRows, preset.filters, columnMeta);
    if (!deferredSearchQuery || !searchPredicate) return columnFiltered;
    const q = deferredSearchQuery.toLowerCase();
    return columnFiltered.filter((row) => searchPredicate(row, q));
  }, [groupFilteredRows, preset.filters, columnMeta, deferredSearchQuery, searchPredicate]);

  const sortedRows = useMemo(
    () => sortGridItems(filteredRows, preset.sortColumns, preset.sortOrders, columnMeta),
    [filteredRows, preset.sortColumns, preset.sortOrders, columnMeta],
  );

  const aggregates = useMemo(() => computeColumnAggregates(filteredRows, columnMeta), [filteredRows, columnMeta]);

  // Same predicate DataGridPresetBar computes internally (`hasColumnOrGroupFilters`
  // + searchQuery) — see that file's `hasActiveFilters` for the canonical
  // definition this mirrors.
  const hasActiveFilters = useMemo(
    () =>
      Object.values(preset.filters).some((v) => (Array.isArray(v) ? v.length > 0 : v.min != null || v.max != null)) ||
      Object.values(preset.groupFilters).some((v) => v.length > 0) ||
      !!searchQuery,
    [preset.filters, preset.groupFilters, searchQuery],
  );

  const handleSort = useCallback(
    (columnKey: string, order: 'asc' | 'desc' | 'none') => {
      const currentIndex = preset.sortColumns.indexOf(columnKey);
      const nextColumns = [...preset.sortColumns];
      const nextOrders = [...preset.sortOrders];

      if (order === 'none') {
        if (currentIndex >= 0) {
          nextColumns.splice(currentIndex, 1);
          nextOrders.splice(currentIndex, 1);
        }
      } else if (currentIndex >= 0) {
        nextOrders[currentIndex] = order;
      } else {
        nextColumns.push(columnKey);
        nextOrders.push(order);
      }

      patchActivePreset({ sortColumns: nextColumns, sortOrders: nextOrders });
    },
    [patchActivePreset, preset.sortColumns, preset.sortOrders],
  );

  const handleFilterChange = useCallback(
    (columnKey: string, value: FilterValue) => {
      patchActivePreset({ filters: { ...preset.filters, [columnKey]: value } });
    },
    [patchActivePreset, preset.filters],
  );

  // The active group-by column, or null when the table isn't grouped.
  const handleToggleGroup = useCallback(
    (columnKey: K) => {
      patchActivePreset({ groupByColumnKey: preset.groupByColumnKey === columnKey ? null : columnKey });
    },
    [patchActivePreset, preset.groupByColumnKey],
  );

  return {
    ...presetApi,
    preset,
    searchQuery,
    setSearchQuery,
    groupConfigs,
    visibleColumns,
    groupFilteredRows,
    sortedRows,
    aggregates,
    hasActiveFilters,
    handleSort,
    handleFilterChange,
    handleToggleGroup,
  };
}
